import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { UPLOAD_LIMITS } from "@rec/shared";
import { useServerText } from "@/i18n";
import { useFormat } from "@/i18n/format";
import {
  abandonUpload,
  findResumable,
  readDurationMs,
  uploadFile,
  type UploadHandle,
} from "@/lib/upload";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";

/**
 * Upload a recording.
 *
 * The file goes straight from the browser to S3 in parts — it never passes
 * through our API, which only signs each part. A two-gigabyte file streamed
 * through Node would hold a request open for minutes and die on the next
 * deploy.
 *
 * Parts are what make it resumable. A single PUT that failed at 90% of a 3 GB
 * file threw away the entire upload; now a dropped connection costs one part,
 * and picking the same file again resumes rather than restarts.
 */
export function UploadButton({
  projectId,
  compact = false,
}: {
  projectId: string | null;
  /**
   * Hide the idle helper text. For tight rows (the Studio sources panel)
   * where the format line would squeeze its neighbours into unreadability —
   * the limits move into the button's tooltip instead. Progress, cancel and
   * errors still render; only the idle caption is dropped.
   */
  compact?: boolean;
}) {
  const { t } = useTranslation(["library", "common"]);
  const f = useFormat();
  const st = useServerText();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const handleRef = useRef<UploadHandle | null>(null);

  const [progress, setProgress] = useState<number | null>(null);
  const [resuming, setResuming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File) {
    setError(null);
    setProgress(0);

    // Checked here as well as on the server so a 4 GB file is refused in the
    // file picker rather than after the browser has spent an hour sending it.
    if (file.size > UPLOAD_LIMITS.maxBytes) {
      setError(
        t("upload.tooLarge", {
          size: f.bytes(file.size),
          limit: f.bytes(UPLOAD_LIMITS.maxBytes),
        }),
      );
      setProgress(null);
      return;
    }

    const durationMs = await readDurationMs(file);

    if (durationMs && durationMs > UPLOAD_LIMITS.maxDurationMs) {
      setError(t("upload.tooLong"));
      setProgress(null);
      return;
    }

    const existing = findResumable(file);
    setResuming(Boolean(existing));

    const handle = uploadFile({
      file,
      projectId,
      durationMs,
      resume: existing ? { recordingId: existing.recordingId } : undefined,
      onProgress: setProgress,
    });

    handleRef.current = handle;

    try {
      await handle.promise;
      await queryClient.invalidateQueries({ queryKey: ["recordings"] });
      // Folder counts include the new video; without this the sidebar reads
      // stale until a reload while every other path updates live.
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") {
        // Cancelling is not an error, and the parts are released by `cancel`.
        return;
      }

      // Deliberately *not* discarded. The parts already on S3 are what makes
      // picking the same file again a resume instead of a restart — throwing
      // them away here would turn every transient failure into starting over,
      // which is the behaviour this replaced.
      setError(
        caught instanceof Error
          ? t("upload.failedResume", { message: st(caught.message) })
          : t("upload.failed"),
      );
    } finally {
      handleRef.current = null;
      setProgress(null);
      setResuming(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function cancel() {
    const handle = handleRef.current;
    if (!handle) return;

    handle.cancel();
    // Discard the parts: cancelling is a decision not to have this recording,
    // unlike a failure, and the parts are billed until they are aborted.
    if (handle.recordingId) await abandonUpload(handle.recordingId);
    await queryClient.invalidateQueries({ queryKey: ["recordings"] });
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <input
        ref={inputRef}
        type="file"
        accept={UPLOAD_LIMITS.acceptedTypes.join(",")}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void upload(file);
        }}
      />

      <Button
        variant="secondary"
        size="sm"
        disabled={progress !== null}
        onClick={() => inputRef.current?.click()}
        title={
          compact
            ? t("upload.limits", { size: f.bytes(UPLOAD_LIMITS.maxBytes) })
            : undefined
        }
      >
        {progress !== null ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <Upload size={14} />
        )}
        {progress !== null
          ? t(resuming ? "upload.resuming" : "upload.uploading", { percent: progress })
          : t("common:actions.upload")}
      </Button>

      {progress !== null ? (
        <>
          <div className="h-1 w-28 overflow-hidden rounded-full bg-bg-subtle">
            <div
              className={cn("h-full rounded-full bg-accent transition-all")}
              style={{ width: `${progress}%` }}
            />
          </div>
          <button
            type="button"
            onClick={() => void cancel()}
            className="flex items-center gap-1 text-[12.5px] text-text-muted hover:text-text"
          >
            <X size={12} />
            {t("common:actions.cancel")}
          </button>
        </>
      ) : compact ? null : (
        // Stated before a file is picked, not as an error afterwards. The size
        // limit exists because of transcription, so it says so — otherwise it
        // reads as an arbitrary rule.
        <span className="text-[12.5px] text-text-faint">
          {t("upload.limits", { size: f.bytes(UPLOAD_LIMITS.maxBytes) })}
        </span>
      )}

      {error ? (
        <span role="alert" className="text-[12.5px] text-danger">
          {error}
        </span>
      ) : null}
    </div>
  );
}
